import { useState, type ReactNode } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import type { DirectoryAccount } from '@workflow/contracts'
import { Avatar, AvatarStack, Button, ENTER, Icons, staggerDelay } from '@workflow/ui'
import { navigate, type PeopleSection } from '@/app/router'
import { describeError } from '@/lib/api/client'
import { useApproveMember, useMembers, useRejectMember, useSession } from '@/lib/api/hooks'
import { formatRelative } from '@/lib/format'
import { personName, realName } from '@/lib/people'
import { PageFrame } from '@/shell/PageFrame'
import { DirectoryPane, InvitesPane } from './DirectoryPane'
import { InviteDialog } from './InviteDialog'
import { RequestsPane } from './RequestsPane'

const SECTION_COPY: Record<PeopleSection, { title: string; description: string }> = {
  requests: { title: 'Requests', description: 'People who submitted their profile and are waiting for a yes or a no.' },
  invites: { title: 'Invites', description: 'Addresses let in who have not signed in yet. Added addresses can sign in immediately.' },
  everyone: { title: 'Everyone', description: 'Staff and players in one directory — they are one account system.' },
}

const faces = (people: readonly DirectoryAccount[]) =>
  people.map((member) => ({ key: member.id, name: personName(member), email: member.email, src: member.pictureUrl }))

/**
 * People — the whole of access, as three rooms rather than one long page:
 * the requests waiting for a decision, the invites not yet taken up, and
 * everyone already in. The page itself is the hallway: a card for each,
 * with its count and faces, and each card opens its own page. Reading the
 * directory is an administrator's ability on the API, so for staff the page
 * is the sentence that says who holds the keys.
 */
export function PeoplePage({ section }: { section?: PeopleSection }) {
  const session = useSession()
  const isAdmin = session.data?.account?.role === 'admin'
  const [inviting, setInviting] = useState(false)

  if (!isAdmin) {
    return (
      <PageFrame title="People" description="Staff and players in one directory — they are one account system.">
        <p className="max-w-[60ch] text-ui text-muted-foreground">
          Access is managed by the administrators, here: who may come in, who is waiting, who was
          turned away. Your own account is under Settings → Account.
        </p>
      </PageFrame>
    )
  }

  const invite = (
    <>
      <Button size="sm" variant="primary" icon={<Icons.plus />} onClick={() => setInviting(true)}>Invite</Button>
      <InviteDialog open={inviting} onClose={() => setInviting(false)} />
    </>
  )

  if (section) {
    const copy = SECTION_COPY[section]
    return (
      <PageFrame
        title={copy.title}
        description={copy.description}
        eyebrow={
          <button
            type="button"
            onClick={() => navigate({ name: 'people' })}
            className="-ml-1 inline-flex items-center gap-1 rounded-full px-1 text-caption text-muted-foreground outline-none transition-colors duration-fast hover:text-foreground focus-visible:ring-1 focus-visible:ring-foreground"
          >
            <Icons.chevronLeft size="sm" /> People
          </button>
        }
        actions={section === 'requests' ? null : invite}
      >
        {section === 'requests' ? <RequestsPane /> : section === 'invites' ? <InvitesPane /> : <DirectoryPane />}
      </PageFrame>
    )
  }

  return (
    <PageFrame title="People" description="Who is waiting, who is invited, and everyone already in.">
      <PeopleHub />
    </PageFrame>
  )
}

/**
 * The hallway: the three rooms as cards — each one whole a way in, numbered
 * like the index it belongs to — and under them the two things done most
 * often, done right here: deciding on a request, and letting someone in.
 */
function PeopleHub() {
  const members = useMembers()
  const all = members.data ?? []
  const loading = members.isLoading
  const requests = all.filter((member) => member.status === 'pending')
    .sort((a, b) => a.updatedAt.localeCompare(b.updatedAt))
  const invites = all.filter((member) => member.status === 'invited')
    .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))
  // The card counts what its page lists: every account, by role.
  const count = (role: DirectoryAccount['role']) => all.filter((member) => member.role === role).length
  const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`

  return (
    <div>
      <div className="grid gap-3 sm:grid-cols-3">
        <RoomCard
          index={0}
          section="requests"
          count={loading ? null : requests.length}
          line={requests.length === 0 ? 'Nothing Waiting On You' : requests.length === 1 ? 'One Person Waiting On You' : `${requests.length} People Waiting On You`}
          people={requests}
          alert={requests.length > 0}
        />
        <RoomCard
          index={1}
          section="invites"
          count={loading ? null : invites.length}
          line={invites.length > 0 ? 'Let In, Not Signed In Yet' : 'No Open Invites'}
          people={invites}
        />
        <RoomCard
          index={2}
          section="everyone"
          count={loading ? null : all.length}
          line={([['player', 'Player', 'Players'], ['staff', 'Staff', 'Staff'], ['admin', 'Admin', 'Admins']] as const)
            .filter(([role]) => count(role) > 0)
            .map(([role, one, many]) => plural(count(role), one, many))
            .join(' · ') || 'No One Yet'}
          people={all.filter((member) => member.status === 'active')}
        />
      </div>
      {members.error ? <p className="mt-3 text-caption text-danger">The directory did not load. Refresh to try again.</p> : null}

      <div className="mt-10">
        <QuickRequests requests={requests} loading={loading} />
      </div>
    </div>
  )
}

/** A quiet editorial heading: the number, the name, a line under. */
function QuickHeading({ title, aside }: { title: string; aside?: ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-3 border-b border-hairline pb-3">
      <h2 className="wf-heading text-lg text-foreground">{title}</h2>
      {aside}
    </div>
  )
}

/**
 * The requests, decided in place: the oldest few with their faces and two
 * buttons each. A decided row leaves the list; the full page, with every
 * profile to read first, is one press away.
 */
function QuickRequests({ requests, loading }: { requests: readonly DirectoryAccount[]; loading: boolean }) {
  const shown = requests.slice(0, 6)
  return (
    <section className="min-w-0">
      <QuickHeading
        title="Waiting On You"
        aside={requests.length > shown.length ? (
          <button type="button" onClick={() => navigate({ name: 'people', section: 'requests' })}
            className="text-caption text-muted-foreground underline-offset-4 outline-none hover:text-foreground hover:underline focus-visible:underline">
            {`See All ${requests.length}`}
          </button>
        ) : null}
      />
      {loading ? null : shown.length === 0 ? (
        <QuickRow
          mark={<QuickGlyph><Icons.complete size="sm" /></QuickGlyph>}
          title={<span className="block text-ui font-medium text-foreground">No One Is Waiting</span>}
          detail="New requests land here the moment a profile is sent"
        />
      ) : (
        <ul className="flex flex-col">
          <AnimatePresence initial={false}>
            {shown.map((member, index) => <QuickRequestRow key={member.id} member={member} index={index} />)}
          </AnimatePresence>
        </ul>
      )}
    </section>
  )
}

function QuickRequestRow({ member, index }: { member: DirectoryAccount; index: number }) {
  const approve = useApproveMember()
  const reject = useRejectMember()
  const busy = approve.isPending || reject.isPending
  const fault = approve.error ?? reject.error
  return (
    <motion.li
      layout="position"
      initial={ENTER.initial}
      animate={{ ...ENTER.animate, transition: { ...ENTER.animate.transition, delay: staggerDelay(index) } }}
      exit={ENTER.exit}
    >
      <QuickRow
        mark={
          <button type="button" onClick={() => navigate({ name: 'people', section: 'requests' })} aria-label={`Review ${personName(member)}`}
            className="rounded-full outline-none focus-visible:ring-1 focus-visible:ring-foreground">
            <Avatar name={personName(member)} email={member.email} src={member.pictureUrl} size="md" />
          </button>
        }
        title={
          <button type="button" onClick={() => navigate({ name: 'people', section: 'requests' })}
            className="block max-w-full truncate text-left text-ui font-medium text-foreground outline-none hover:underline focus-visible:underline">
            {personName(member)}
          </button>
        }
        detail={[`Asked ${formatRelative(member.updatedAt)}`, realName(member)].filter(Boolean).join(' · ')}
        action={
          <>
            <Button size="sm" variant="danger" icon={<Icons.close />} disabled={busy} busy={reject.isPending} onClick={() => reject.mutate(member.id)}
              className="max-sm:w-[var(--control-sm)] max-sm:px-0" aria-label={`Reject ${personName(member)}`}>
              <span className="max-sm:sr-only">Reject</span>
            </Button>
            <Button size="sm" variant="primary" icon={<Icons.approve />} disabled={busy} busy={approve.isPending} onClick={() => approve.mutate(member.id)}
              className="max-sm:w-[var(--control-sm)] max-sm:px-0" aria-label={`Approve ${personName(member)}`}>
              <span className="max-sm:sr-only">Approve</span>
            </Button>
          </>
        }
      />
      {fault ? <p className="pb-3 text-caption text-danger">{describeError(fault)}</p> : null}
    </motion.li>
  )
}

/** One line of the quick list: a 40px mark, a name over a small line, and the action at the row's end. */
function QuickRow({ mark, title, detail, action }: { mark: ReactNode; title: ReactNode; detail?: ReactNode; action?: ReactNode }) {
  return (
    <div className="flex min-h-[4.75rem] items-center gap-4 border-b border-hairline py-4">
      <span className="flex size-10 shrink-0 items-center justify-center">{mark}</span>
      <span className="min-w-0 flex-1">
        {title}
        {detail ? <span className="block truncate text-caption text-muted-foreground">{detail}</span> : null}
      </span>
      {action ? <span className="flex shrink-0 items-center gap-1.5">{action}</span> : null}
    </div>
  )
}

const QuickGlyph = ({ children }: { children: ReactNode }) => (
  <span className="flex size-10 items-center justify-center rounded-full bg-tint text-muted-foreground" aria-hidden>{children}</span>
)

const ROOM_NUMBER: Record<PeopleSection, string> = { requests: '01', invites: '02', everyone: '03' }

/** One room as a card: its number and name, the count large, a sentence, faces — and the whole of it a way in. */
function RoomCard({ index, section, count, line, people, alert = false }: {
  index: number
  section: PeopleSection
  count: number | null
  line: string
  people: readonly DirectoryAccount[]
  alert?: boolean
}) {
  return (
    <motion.button
      type="button"
      initial={ENTER.initial}
      animate={{ ...ENTER.animate, transition: { ...ENTER.animate.transition, delay: staggerDelay(index) } }}
      onClick={() => navigate({ name: 'people', section })}
      aria-label={`${SECTION_COPY[section].title}: ${count ?? 'loading'}. ${line}`}
      className="group flex flex-col rounded-[28px] sm:min-h-[12.5rem] bg-tint p-5 text-left outline-none transition-colors duration-base hover:bg-foreground/[0.06] focus-visible:ring-1 focus-visible:ring-foreground"
    >
      <span className="flex w-full items-start justify-between gap-2">
        <span className="flex items-baseline gap-2.5">
          <span className="font-mono text-micro text-muted-foreground tabular-nums">{ROOM_NUMBER[section]}</span>
          <span className="text-micro text-foreground uppercase">{SECTION_COPY[section].title}</span>
        </span>
        {/* The way in, said by shape: an arrow in a circle that fills on hover. */}
        <span
          aria-hidden
          className="flex size-8 shrink-0 items-center justify-center rounded-full border border-hairline text-muted-foreground transition-colors duration-base group-hover:border-foreground group-hover:bg-foreground group-hover:text-background"
        >
          <Icons.disclosure size="sm" />
        </span>
      </span>
      <span className="mt-2 flex items-center gap-2.5 sm:mt-4">
        <span className="font-display text-4xl leading-none tabular-nums text-foreground sm:text-5xl">{count ?? '–'}</span>
        {alert ? <span className="size-2 rounded-full bg-status-amber" aria-hidden /> : null}
      </span>
      <span className="mt-2 text-caption text-muted-foreground">{line}</span>
      <span className="mt-auto pt-4 sm:pt-5">
        {people.length > 0 ? <AvatarStack max={5} people={faces(people)} /> : <span className="block h-5" />}
      </span>
    </motion.button>
  )
}
